import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorState from '../../../commodity/components/ui/ErrorState';

import {
  Booking,
  acceptReschedule as apiAccept,
  confirmBooking as apiConfirm,
  getBookings,
} from '../api/booking';
import { closingLabel } from '../vocabulary/learningVocabulary';

interface PendingItem {
  booking: Booking;
  kind: 'confirm' | 'accept-reschedule';
}

interface Props {
  enrollmentIds: string[];
  /** The viewer's role in the booking — actions appear only for slots the OTHER party proposed. */
  actor: 'teacher' | 'parent';
}

const fmt = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const slotTitle = (b: Booking) =>
  b.milestoneSequence === 8 ? closingLabel() : `Sesión ${b.milestoneSequence}`;

/**
 * Surfaces bookings that need THIS user's action (a slot the counterparty
 * proposed, or a reschedule they proposed) across all their enrollments, with a
 * one-click Confirmar/Aceptar — so a teacher no longer has to dig into each
 * enrollment's schedule to find a pending confirmation.
 */
const PendingConfirmations: React.FC<Props> = ({ enrollmentIds, actor }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  /** Some enrollments could not be read; the rest of the list is still true. */
  const [partialFailures, setPartialFailures] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (enrollmentIds.length === 0) {
      setItems([]);
      return;
    }
    setError(null);
    try {
      // Each enrollment's failure used to become an empty list right here, so a
      // total outage produced "nothing to confirm" rather than an error: the
      // outer catch never fired because nothing ever rejected. Failures are
      // counted instead, and the panel says which case it is.
      const results = await Promise.allSettled(
        enrollmentIds.map((id) => getBookings(id).then((r) => r.bookings)),
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0 && failures.length === results.length) {
        const first = failures[0] as PromiseRejectedResult;
        throw first.reason instanceof Error ? first.reason : new Error(String(first.reason));
      }
      setPartialFailures(failures.length);

      const lists = results.map((r) => (r.status === 'fulfilled' ? r.value : []));
      const pending: PendingItem[] = [];
      for (const b of lists.flat()) {
        if (
          b.state === 'Requested' &&
          b.scheduledStart &&
          b.requestedBy &&
          b.requestedBy !== actor
        ) {
          pending.push({ booking: b, kind: 'confirm' });
        } else if (
          b.state === 'Rescheduled' &&
          b.rescheduleProposedBy &&
          b.rescheduleProposedBy !== actor
        ) {
          pending.push({ booking: b, kind: 'accept-reschedule' });
        }
      }
      pending.sort((a, b) =>
        (a.booking.scheduledStart || a.booking.proposedStart || '').localeCompare(
          b.booking.scheduledStart || b.booking.proposedStart || '',
        ),
      );
      setItems(pending);
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar las confirmaciones pendientes');
    } finally {
      setLoading(false);
    }
  }, [enrollmentIds, actor]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (item: PendingItem) => {
    setBusy(item.booking.bookingId);
    setError(null);
    try {
      if (item.kind === 'confirm') await apiConfirm(item.booking.bookingId);
      else await apiAccept(item.booking.bookingId);
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo completar la confirmación');
    } finally {
      setBusy(null);
    }
  };

  // Three distinct states that all used to render as nothing at all (S1.3).
  //
  // While loading, an empty list is not an answer — the panel stayed invisible
  // and the teacher read "no sessions to confirm" from silence. And a load that
  // *failed* left items empty too, so this early return swallowed the error
  // below it: the panel simply ceased to exist, with no way to tell that from
  // having nothing to confirm.
  if (loading) return null;
  if (error) {
    return (
      <div style={panelStyle}>
        <ErrorState
          action="cargar las sesiones por confirmar"
          error={error}
          onRetry={load}
          compact
        />
      </div>
    );
  }
  if (items.length === 0 && partialFailures === 0) return null;

  return (
    <div style={panelStyle}>
      <strong style={{ display: 'block', marginBottom: 8 }}>
        Sesiones por confirmar ({items.length})
      </strong>
      {partialFailures > 0 && (
        <div style={{ color: '#d9480f', fontSize: 13, marginBottom: 8 }}>
          No pudimos revisar {partialFailures}{' '}
          {partialFailures === 1 ? 'inscripción' : 'inscripciones'}, así que esta lista puede estar
          incompleta.
        </div>
      )}
      {error && <div style={{ color: '#c92a2a', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(({ booking, kind }) => (
          <div key={booking.bookingId} style={rowStyle}>
            <div style={{ fontSize: 14 }}>
              <strong>{slotTitle(booking)}</strong>
              {booking.courseTitle ? ` · ${booking.courseTitle}` : ''}
              <span style={{ color: '#555', marginLeft: 8 }}>
                {fmt(kind === 'confirm' ? booking.scheduledStart : booking.proposedStart)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => act({ booking, kind })}
                disabled={!!busy}
                style={confirmBtn(!!busy)}
              >
                {busy === booking.bookingId ? '...' : kind === 'confirm' ? 'Confirmar' : 'Aceptar'}
              </button>
              <button
                onClick={() => navigate(`/enrollments/${booking.enrollmentId}`)}
                style={ghostBtn}
              >
                Ver
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const panelStyle: React.CSSProperties = {
  border: '1px solid #ffd43b',
  background: '#fff9db',
  borderRadius: 8,
  padding: 14,
  marginBottom: 20,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  background: '#fff',
  border: '1px solid #ffe8a3',
  borderRadius: 6,
  padding: '8px 12px',
};

const confirmBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 14px',
  border: 'none',
  borderRadius: 4,
  background: disabled ? '#a0c4e8' : '#2b8a3e',
  color: '#fff',
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const ghostBtn: React.CSSProperties = {
  padding: '6px 12px',
  border: '1px solid #1971c2',
  borderRadius: 4,
  background: '#fff',
  color: '#1971c2',
  fontSize: 13,
  cursor: 'pointer',
};

export default PendingConfirmations;
